import { PersonRounded } from '@mui/icons-material';
import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// Assets.
import { ReactComponent as AccmeLogo } from 'globals/images/accme-logo.svg';

// Components.
import { Button, Link } from 'components';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';

// Store + Core.
import { ButtonVariant } from 'core';
import { userProfileFullNameSelector } from 'store/profile/selectors';

// Hooks.
import { useAuth, useAuthLogin, useAuthLogout, useClickEvent } from 'hooks';

// Styles.
import styles from './DesktopHeader.module.scss';

export const DesktopHeader = (): ReactElement => {
  const { pathname } = useLocation();
  const matchProfilePage: boolean = pathname.indexOf('profile') > -1;
  const [account, { isAuthenticated }] = useAuth();
  const login = useAuthLogin();
  const logout = useAuthLogout();
  const fireEvent = useClickEvent({ Event: 'navigation', EventCategory: 'Navigation' });
  const isIconBreakpoint = useMediaQuery(`(min-width:${'1100px'})`);

  // Selectors
  const fullName: string = useSelector(userProfileFullNameSelector);

  return (
    <header>
      <div className={styles.masthead}>
        <div className={styles.logo}>
          <Link to="/">
            <AccmeLogo className={styles.logoImage} />
          </Link>
        </div>
        <nav>
          <ul>
            <li>
              <Button className={styles.link} component={Link} to="https://content.cmepassport.org/" target="_blank">
                State CME Requirements
              </Button>
            </li>
            <li>
              <Button
                className={styles.link}
                component={Link}
                onClick={() => fireEvent({ EventAction: 'Header', EventLabel: '/activity/search' })}
                to="/activity/search"
              >
                Find Accredited CME
              </Button>
            </li>
            {isAuthenticated ? (
              <>
                {!matchProfilePage && (
                  <li>
                    <Button
                      className={styles.link}
                      component={Link}
                      onClick={() => fireEvent({ EventAction: 'Header', EventLabel: '/profile/transcripts' })}
                      to="/profile/transcripts"
                    >
                      View My Transcripts
                    </Button>
                  </li>
                )}
                <li>
                  <Button
                    className={styles.link}
                    component={Link}
                    onClick={() => {
                      fireEvent({ EventAction: 'Header', EventLabel: 'Sign Out' });
                      logout();
                    }}
                    title="Sign Out"
                  >
                    Sign Out
                  </Button>
                </li>
                <li>
                  {isIconBreakpoint ? (
                    <Button
                      variant={ButtonVariant.Secondary}
                      className={styles.button}
                      component={Link}
                      onClick={() => {
                        fireEvent({ EventAction: 'Header', EventLabel: '/profile' });
                      }}
                      title={account?.name}
                      to="/profile"
                    >
                      <Box className={styles.flexBox}>
                        <PersonRounded />
                        <Typography noWrap className={styles.nameText}>
                          {fullName}
                        </Typography>
                      </Box>
                    </Button>
                  ) : (
                    <IconButton
                      size="large"
                      className={styles.iconButton}
                      component={Link}
                      onClick={() => {
                        fireEvent({ EventAction: 'Header', EventLabel: '/profile' });
                      }}
                      title={account?.name}
                      to="/profile"
                    >
                      <PersonRounded />
                    </IconButton>
                  )}
                </li>
              </>
            ) : (
              <>
                <li>
                  <Button
                    className={styles.link}
                    component={Link}
                    onClick={() => {
                      fireEvent({ EventAction: 'Header', EventLabel: 'Sign In' });
                      login();
                    }}
                    title="Sign In"
                  >
                    Sign In
                  </Button>
                </li>
                <Button
                  variant={ButtonVariant.Secondary}
                  className={styles.button}
                  component={Link}
                  onClick={() => {
                    fireEvent({ EventAction: 'Header', EventLabel: '/register' });
                  }}
                  title="Create an Account"
                  to="/register"
                >
                  <span>Create an Account</span>
                </Button>
              </>
            )}
          </ul>
        </nav>
      </div>
    </header>
  );
};
